/**
 * Renewal invoice lifecycle — display + form rules for the dashboard's
 * Upcoming Renewals widget.
 *
 * The backend (`backend/renewal_lifecycle.py`) derives each row's stage
 * and label; this module only decides how a stage LOOKS and what the
 * operator's update form may submit. Nothing here re-derives "overdue":
 * a row is invoice-overdue only when the server says so, which it never
 * does once an invoice has been issued.
 */

export const PAYMENT_OPTIONS = [
  { value: "awaiting_payment", label: "Awaiting payment" },
  { value: "part_paid", label: "Part paid" },
  { value: "paid", label: "Paid" },
  { value: "disputed", label: "Disputed" },
  { value: "written_off", label: "Written off" },
];

export const SOURCE_OPTIONS = [
  { value: "xero", label: "Xero" },
  { value: "finops", label: "FinOps" },
  { value: "manual", label: "Manual / other" },
];

// Pill styling per server tone. Red is reserved for things that need
// action (invoice overdue, disputed); awaiting payment is amber, not red.
const TONE_CLASSES = {
  neutral: "bg-[#EFF6FF] text-[#1D4ED8] border-[#BFDBFE]",
  danger: "bg-[#FEF2F2] text-[#B91C1C] border-[#FECACA]",
  warning: "bg-[#FFF7ED] text-[#9A3412] border-[#FED7AA]",
  success: "bg-[#F0FDF4] text-[#15803D] border-[#BBF7D0]",
  muted: "bg-[#F5F5F5] text-[#525252] border-[#E5E5E5]",
};

export function toneClasses(tone) {
  return TONE_CLASSES[tone] || TONE_CLASSES.muted;
}

function isIsoDate(v) {
  return typeof v === "string" && /^\d{4}-\d{2}-\d{2}$/.test(v);
}

/**
 * Client-side mirror of `renewal_lifecycle.validate_update`. The server
 * is authoritative; this exists so the operator sees the problem next
 * to the field instead of after a round trip. Returns an error string,
 * or null when the form may be submitted.
 */
export function validateInvoiceForm(form, todayIso) {
  const issued = form.invoice_issued_on || "";
  const paid = form.paid_on || "";
  const status = form.payment_status || "awaiting_payment";
  if (issued && !isIsoDate(issued)) return "Invoice issued date must be a date.";
  if (paid && !isIsoDate(paid)) return "Paid date must be a date.";
  if (issued && todayIso && issued > todayIso) return "Invoice issued date cannot be in the future.";
  if (status === "paid" && !paid) return "Enter the date the invoice was paid.";
  if (paid) {
    if (status !== "paid" && status !== "part_paid") {
      return "A paid date only applies when the payment status is Paid or Part paid.";
    }
    if (todayIso && paid > todayIso) return "Paid date cannot be in the future.";
    if (issued && paid < issued) return "Paid date cannot be before the invoice issued date.";
  }
  return null;
}

/** Pre-fill the update form from the row's current lifecycle. */
export function formFromLifecycle(lc) {
  const l = lc || {};
  return {
    invoice_issued_on: l.invoice_issued_on || "",
    invoice_number: l.invoice_number || "",
    invoice_source: l.invoice_source || "",
    payment_status: l.payment_status || "awaiting_payment",
    paid_on: l.paid_on || "",
    payment_note: l.payment_note || "",
  };
}

/** "in 3d" / "today" / "3d ago" for the renewal-date cell. */
export function relativeDays(n) {
  if (n === 0) return "today";
  if (n > 0) return `in ${n}d`;
  return `${-n}d ago`;
}
